import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Typography,
  message,
} from 'antd'
import dayjs, { type Dayjs } from 'dayjs'
import { useEffect, useRef, useState } from 'react'

import {
  type DesiredBookingTeacher,
  type DesiredUploadedSlot,
  createAdminDesiredAppointment,
  getDesiredBookingTeachers,
  getDesiredUploadedSlots,
} from '~/services/appointment'
import { IStudent } from '~/types/student'

interface Values {
  teacherId: number
  date: Dayjs
  start: number
  remark?: string
}

export default function DesiredBookingModal({
  student,
  onClose,
}: {
  student: IStudent
  onClose: () => void
}) {
  const [form] = Form.useForm<Values>()
  const [query, setQuery] = useState('')
  const [teachers, setTeachers] = useState<DesiredBookingTeacher[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const submitting = useRef(false)
  const selectedTeacherId = Form.useWatch('teacherId', form)
  const selectedTeacher = useRef<DesiredBookingTeacher>()
  const start = Form.useWatch('start', form)
  const date = Form.useWatch('date', form)
  const from = date?.startOf('day').valueOf()
  const to = date?.startOf('day').add(1, 'day').valueOf()
  const [uploaded, setUploaded] = useState<{
    key: string
    slots: DesiredUploadedSlot[]
  } | null>(null)
  const [scheduleError, setScheduleError] = useState(false)
  const [retry, setRetry] = useState(0)
  const scheduleKey = `${selectedTeacherId}:${from}:${to}`
  const scheduleReady = uploaded?.key === scheduleKey
  const overlapsUpload = (at: number) =>
    uploaded?.slots.some((slot) => slot.from < at + 1800000 && slot.to > at)
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  useEffect(() => {
    let active = true
    setUploaded(null)
    setScheduleError(false)
    if (selectedTeacherId && from !== undefined && to !== undefined) {
      getDesiredUploadedSlots(selectedTeacherId, from, to)
        .then(({ data }) => {
          if (active) setUploaded({ key: scheduleKey, slots: data })
        })
        .catch(() => {
          if (active) setScheduleError(true)
        })
    }
    return () => {
      active = false
    }
  }, [selectedTeacherId, from, to, scheduleKey, retry])

  useEffect(() => {
    let active = true
    setLoading(true)
    const timer = setTimeout(() => {
      getDesiredBookingTeachers(query)
        .then(({ data }) => {
          if (active) setTeachers(data)
        })
        .catch(() => {
          if (active) {
            setTeachers([])
            message.error('加载老师失败，请重新搜索')
          }
        })
        .finally(() => {
          if (active) setLoading(false)
        })
    }, 250)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [query])

  const submit = async () => {
    if (submitting.current) return
    const values = await form.validateFields().catch(() => null)
    if (!values || submitting.current) return
    submitting.current = true
    setSaving(true)
    try {
      const { data } = await createAdminDesiredAppointment({
        studentId: student.id,
        teacherId: values.teacherId,
        classStartTime: values.start,
        remark: values.remark?.trim() || '',
      })
      Modal.success({
        title: '期望申请已发送，尚未预约成功',
        content: `申请 #${data.id}，老师需在 ${dayjs(data.desiredExpiresAt).format('YYYY-MM-DD HH:mm')}（${timezone}）前接受。请联系老师确认；可在预约管理中查看接受、拒绝或过期结果。`,
        okText: '知道了',
      })
      onClose()
    } catch (error: any) {
      const detail = error?.response?.data
      message.error(
        typeof detail === 'string'
          ? detail
          : detail?.message ||
              '申请未确认成功，请查看预约记录后再试，避免重复提交',
      )
    } finally {
      submitting.current = false
      setSaving(false)
    }
  }

  return (
    <Modal
      title="代约期望时间"
      open
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      okButtonProps={{ disabled: !scheduleReady }}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      okText="发送申请，等待老师确认"
      cancelText="取消"
      width={560}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          message="支持当天临时申请，不受提前 12 小时限制"
          description="仅创建单次期望申请，不立即扣课时卡。老师已上传的单次和每周固定时间不可选。老师接受后才预约成功；提交后 24 小时或开课时仍未接受将自动过期。学生需已有当前教材及可用课时卡。"
        />
        <Typography.Text>
          学生：{student.name}（ID: {student.id}）
        </Typography.Text>
        <Form
          form={form}
          layout="vertical"
          disabled={saving}
          initialValues={{ date: dayjs().startOf('day') }}
        >
          <Form.Item
            name="teacherId"
            label="老师"
            rules={[{ required: true, message: '请选择老师' }]}
          >
            <Select
              showSearch
              filterOption={false}
              onSearch={setQuery}
              loading={loading}
              placeholder="搜索老师姓名或 ID（仅显示审核通过的老师）"
              options={teachers.map((t) => ({
                value: t.id,
                label: `${t.name}（ID: ${t.id} · 每节 ${t.cardsPerLesson} 课时）`,
              }))}
              onChange={(id) => {
                selectedTeacher.current = teachers.find((t) => t.id === id)
                form.setFieldValue('start', undefined)
              }}
            />
          </Form.Item>
          <Form.Item
            name="date"
            label={`期望上课日期（${timezone}）`}
            rules={[{ required: true, message: '请选择日期' }]}
          >
            <DatePicker
              disabled={!selectedTeacherId}
              allowClear={false}
              disabledDate={(value) => value.isBefore(dayjs(), 'day')}
              onChange={() => form.setFieldValue('start', undefined)}
              style={{ width: '100%' }}
            />
          </Form.Item>
          {scheduleError && (
            <Alert
              type="error"
              showIcon
              message="加载老师排课失败，暂不能选择时间"
              action={
                <Button
                  size="small"
                  onClick={() => setRetry((value) => value + 1)}
                >
                  重试
                </Button>
              }
            />
          )}
          <Form.Item
            name="start"
            label={`期望上课时间（${timezone}）`}
            rules={[
              { required: true, message: '请选择期望时间' },
              {
                validator: (_, value: number | undefined) => {
                  if (value === undefined) return Promise.resolve()
                  if (!scheduleReady)
                    return Promise.reject(new Error('请等待老师排课加载完成'))
                  if (
                    value <= Date.now() ||
                    value % 1800000 !== 0 ||
                    value < from! ||
                    value >= to!
                  ) {
                    return Promise.reject(
                      new Error('请选择当天未来的整点或半点'),
                    )
                  }
                  return overlapsUpload(value)
                    ? Promise.reject(
                        new Error('该时间老师已上传排课，请选择其他时间'),
                      )
                    : Promise.resolve()
                },
              },
            ]}
          >
            <Select
              disabled={!scheduleReady}
              showSearch
              optionFilterProp="label"
              virtual={false}
              loading={!!selectedTeacherId && !scheduleReady && !scheduleError}
              placeholder="请选择时间（已上传排课不可选）"
              options={
                from === undefined || to === undefined
                  ? []
                  : Array.from(
                      { length: Math.ceil((to - from) / 1800000) },
                      (_, i) => {
                        const at = from + i * 1800000
                        const uploaded = overlapsUpload(at)
                        return {
                          value: at,
                          label: `${dayjs(at).format('HH:mm')}${uploaded ? '（老师已上传）' : ''}`,
                          disabled:
                            !scheduleReady || at <= Date.now() || uploaded,
                        }
                      },
                    )
              }
            />
          </Form.Item>
          <Form.Item name="remark" label="课堂备注（选填，老师和学生可见）">
            <Input.TextArea
              rows={3}
              maxLength={1000}
              showCount
              placeholder="例如：6 岁，零基础，试听课"
            />
          </Form.Item>
          {selectedTeacherId && start && (
            <Alert
              type="warning"
              showIcon
              message="请确认后发送"
              description={`${student.name} → ${selectedTeacher.current?.name || selectedTeacherId}，${dayjs(start).format('YYYY-MM-DD HH:mm')}（${timezone}），单次 30 分钟。发送后请主动联系老师，不要向学生承诺已预约成功。`}
            />
          )}
        </Form>
      </Space>
    </Modal>
  )
}
